/**
 * iMessage-style comment bubble for the Word Add-in task pane.
 * Own comments right-aligned (primary), others left-aligned (neutral).
 * Optimized for narrow task pane width.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle, Circle, Trash2, MapPin, Paperclip, Download, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react'
import { getAttachmentDownloadUrl } from '../../services/commentsApi'
import type { NegotiationComment, NegotiationParty } from '../../services/negotiationsApi'
import MarkdownContent from './MarkdownContent'

// Allowed emoji set for reactions (must match backend)
const REACTION_EMOJIS = ['👍', '✅', '❌', '👀', '🎉', '❓'] as const

interface CommentItemProps {
  comment: NegotiationComment
  parties: NegotiationParty[]
  negotiationId: number
  currentVersion: number
  isAuthor: boolean
  onResolve: (commentId: number) => void
  onDelete: (commentId: number) => void
  onClick: (comment: NegotiationComment) => void
  onReply?: (commentId: number) => void
  onReaction?: (commentId: number, emoji: string, add: boolean) => void
  isExpanded?: boolean
  onToggleExpand?: (commentId: number) => void
  isReply?: boolean
  actorId?: string
  /** Whether this comment is unread (new since last visit) */
  isUnread?: boolean
  /** Number of unread replies in this thread (root comments only) */
  unreadReplyCount?: number
}

/** Format file size in human-readable format */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Format a timestamp using i18n */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatTime(iso: string, t: (key: string, options?: any) => string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)

  if (diffMin < 1) return t('time.now', 'now')
  if (diffMin < 60) return t('time.minutesAgo', { count: diffMin, defaultValue: '{{count}}m' })
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return t('time.hoursAgo', { count: diffH, defaultValue: '{{count}}h' })
  const diffD = Math.floor(diffH / 24)
  if (diffD < 7) return t('time.daysAgo', { count: diffD, defaultValue: '{{count}}d' })
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Get party color for a comment author */
function getAuthorColor(
  comment: NegotiationComment,
  parties: NegotiationParty[],
): string {
  if (!comment.author) return '#ADB5BD'
  const party = parties.find((p) => p.actor?.koblink_id === comment.author?.koblink_id)
  return party?.color || '#ADB5BD'
}

function getAuthorInitial(comment: NegotiationComment): string {
  const name = comment.author?.display_name
  if (!name) return '?'
  return name.charAt(0).toUpperCase()
}

/** Get visibility badge color class */
function getVisibilityClass(visibility: string): string {
  switch (visibility) {
    case 'INTERNAL':
      return 'bg-amber-100/80 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
    case 'ALL':
      return 'bg-blue-100/80 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    case 'SPECIFIC':
      return 'bg-purple-100/80 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    default:
      return 'bg-neutral-100 text-neutral-600'
  }
}

/** Own bubble color = visibility-based so you instantly see WHO you sent it to */
const VISIBILITY_COLORS: Record<string, string> = {
  INTERNAL: '#F59E0B', // amber — internal/team only
  ALL: '#3B82F6',      // blue  — all parties
}

function getOwnBubbleColor(
  comment: NegotiationComment,
  parties: NegotiationParty[],
): string {
  if (comment.visibility === 'SPECIFIC') {
    // Use the first TARGET party's color (skip the sender's own party)
    const target = comment.visible_to.find(v => {
      const p = parties.find(pp => pp.tag_name === v.tag_name)
      return p && p.actor?.koblink_id !== comment.author?.koblink_id
    })
    if (target) {
      const p = parties.find(pp => pp.tag_name === target.tag_name)
      if (p?.color) return p.color
    }
    const first = comment.visible_to[0]
    if (first) {
      const p = parties.find(pp => pp.tag_name === first.tag_name)
      if (p?.color) return p.color
    }
    return '#8B5CF6' // purple fallback
  }
  return VISIBILITY_COLORS[comment.visibility] || '#6B7280'
}

/** Get visibility label */
function getVisibilityLabel(
  comment: NegotiationComment,
  t: (key: string) => string,
): string {
  switch (comment.visibility) {
    case 'INTERNAL':
      return t('comments.internal')
    case 'ALL':
      return t('comments.allParties')
    case 'SPECIFIC':
      return comment.visible_to.map((p) => p.party_label).join(', ') || t('comments.specific')
    default:
      return ''
  }
}

export function CommentItem({
  comment,
  parties,
  negotiationId,
  currentVersion,
  isAuthor,
  onResolve,
  onDelete,
  onClick,
  onReply,
  onReaction,
  isExpanded,
  onToggleExpand,
  isReply = false,
  actorId,
  isUnread = false,
  unreadReplyCount = 0,
}: CommentItemProps) {
  const { t } = useTranslation()
  const [showHistory, setShowHistory] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const isSystem = comment.comment_type === 'SYSTEM'
  const authorColor = getAuthorColor(comment, parties)
  const authorInitial = getAuthorInitial(comment)
  const hasAnchor = !!comment.word_cc_tag || (comment.anchors?.length > 0 && !!comment.anchors[0].anchor_text)

  // Inactive: comment is 2+ versions old
  const isInactive = comment.version <= currentVersion - 2

  // Reply count from threaded API
  const replyCount = comment.replies?.length ?? comment.reply_count ?? 0

  // Bubble alignment and colors
  // Own (right): visibility-based color — INTERNAL=amber, ALL=blue, SPECIFIC=target party
  // Others (left): sender's party color
  const isMine = isAuthor
  const bubbleMaxWidth = isReply ? 'max-w-[78%]' : 'max-w-[85%]'
  const bubbleBg = isMine ? getOwnBubbleColor(comment, parties) : authorColor
  const bubbleRounding = isMine
    ? 'rounded-2xl rounded-br-sm'
    : 'rounded-2xl rounded-bl-sm'

  // Content history for edit popover
  const contentHistory: Array<{ content: string; edited_at: string }> = (comment as unknown as Record<string, unknown>).content_history as Array<{ content: string; edited_at: string }> ?? []

  // System events render as centered, muted event lines (like iMessage "X left the group")
  if (isSystem) {
    return (
      <div id={`comment-${comment.id}`} className="animate-fade-in px-4 py-1">
        <div className="flex items-center justify-center gap-1.5">
          <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
          <span className="text-[9px] text-neutral-400 dark:text-neutral-500 italic whitespace-nowrap">
            {comment.content}
          </span>
          <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
        </div>
        <div className="text-center mt-0.5">
          <span className="text-[8px] text-neutral-400">{formatTime(comment.created_at, t)}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      id={`comment-${comment.id}`}
      className={`${isReply ? 'ml-5' : ''} animate-fade-in ${isUnread ? 'border-l-2 border-l-primary-400 bg-primary-500/5' : ''}`}
    >
      {/* Chat bubble row */}
      <div
        className={`flex items-end gap-1.5 px-2 py-0.5 transition-opacity duration-200 ${
          isMine ? 'flex-row-reverse' : 'flex-row'
        } ${comment.resolved ? 'opacity-80 bg-neutral-50 dark:bg-neutral-800/50 rounded' : isInactive ? 'opacity-50' : ''}`}
      >
        {/* Avatar (smaller for task pane) */}
        {!isReply && (
          <div
            className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 text-[8px] font-semibold text-white"
            style={{ backgroundColor: authorColor }}
            title={comment.author?.display_name || ''}
          >
            {authorInitial}
          </div>
        )}

        {/* Bubble */}
        <div className={`group ${bubbleMaxWidth} ${isReply && !isMine ? 'ml-5' : ''} ${isReply && isMine ? 'mr-5' : ''}`}>
          {/* Author name (only for others, not replies) */}
          {!isMine && !isReply && (
            <div className="text-[9px] font-medium text-neutral-400 dark:text-neutral-500 mb-0.5 ml-0.5">
              {comment.author?.display_name || '—'}
            </div>
          )}

          <div
            className={`relative px-2.5 py-1.5 ${bubbleRounding} shadow-sm text-white ${
              hasAnchor ? 'cursor-pointer' : ''
            }`}
            style={{ backgroundColor: bubbleBg }}
            onClick={hasAnchor ? () => onClick(comment) : undefined}
          >
            {/* Anchor text (quoted document reference) */}
            {comment.anchors?.length > 0 && comment.anchors[0].anchor_text ? (
              <div
                className="flex items-start gap-1.5 mb-1.5 rounded-md px-1.5 py-1 -mx-1 cursor-pointer bg-black/15 hover:bg-black/25 transition-colors"
                onClick={(e) => { e.stopPropagation(); onClick(comment) }}
                title={t('comments.scrollToMarker', 'Scroll to marker')}
              >
                <MapPin size={10} className="mt-0.5 flex-shrink-0 text-white/70" />
                <span className="text-[10px] italic leading-snug text-white/90 border-l-2 border-white/40 pl-1.5">
                  &ldquo;{comment.anchors[0].anchor_text.length > 60
                    ? comment.anchors[0].anchor_text.slice(0, 60) + '…'
                    : comment.anchors[0].anchor_text}&rdquo;
                  {comment.anchors[0].anchor_paragraph != null && (
                    <span className="text-white/60 ml-0.5">
                      &sect;{comment.anchors[0].anchor_paragraph + 1}
                    </span>
                  )}
                </span>
              </div>
            ) : hasAnchor ? (
              <div className="flex items-center gap-0.5 mb-0.5 text-white/60">
                <MapPin size={10} />
                <span className="text-[9px]">{t('comments.scrollToMarker')}</span>
              </div>
            ) : null}

            {/* Content with markdown + @mentions */}
            {comment.is_deleted ? (
              <p className="text-[11px] italic text-white/60">{t('comments.delete')}</p>
            ) : (
              <MarkdownContent
                content={comment.content}
                mentions={comment.mentions}
                parties={parties}
                resolved={comment.resolved}
              />
            )}

            {/* Attachments */}
            {comment.comment_attachments?.length > 0 && (
              <div className="flex flex-wrap gap-0.5 mt-1">
                {comment.comment_attachments.map((att) => (
                  <a
                    key={att.id}
                    href={getAttachmentDownloadUrl(negotiationId, comment.id, att.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] transition-colors bg-white/10 hover:bg-white/20 text-white/80"
                    title={`${att.filename} (${formatFileSize(att.file_size)})`}
                  >
                    <Paperclip size={7} />
                    <span className="truncate max-w-[60px]">{att.filename}</span>
                    <Download size={7} className="shrink-0" />
                  </a>
                ))}
              </div>
            )}

            {/* Hover actions overlay — pill with backdrop for visibility */}
            {!comment.is_deleted && (
              <div className={`absolute -top-1.5 ${isMine ? 'left-0' : 'right-0'} flex items-center gap-0.5 rounded-full bg-white/90 dark:bg-neutral-900/90 shadow-md border border-neutral-200/50 dark:border-neutral-700/50 px-0.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                {onReply && !isReply && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onReply(comment.id)
                    }}
                    className="p-0.5 rounded-full transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400"
                    title={t('comments.reply')}
                  >
                    <MessageSquare size={12} />
                  </button>
                )}
                {isAuthor && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onResolve(comment.id)
                      }}
                      className="p-0.5 rounded-full transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-500 hover:text-success-600 dark:text-neutral-400"
                      title={comment.resolved ? t('comments.unresolve') : t('comments.resolve')}
                    >
                      {comment.resolved ? (
                        <Circle size={12} />
                      ) : (
                        <CheckCircle size={12} />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(comment.id)
                      }}
                      className="p-0.5 rounded-full transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 text-neutral-500 hover:text-red-500 dark:text-neutral-400"
                      title={t('comments.delete')}
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Reactions row */}
          {(comment.reactions?.length > 0 || onReaction) && !comment.is_deleted && (
            <div className={`flex items-center gap-0.5 mt-0.5 px-0.5 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
              {/* Existing reactions */}
              {comment.reactions?.map(r => (
                <button
                  key={r.emoji}
                  onClick={(e) => {
                    e.stopPropagation()
                    onReaction?.(comment.id, r.emoji, !r.reacted_by_me)
                  }}
                  className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full text-[9px] transition-all
                    ${r.reacted_by_me
                      ? 'bg-primary-100 dark:bg-primary-900/40 border border-primary-300 dark:border-primary-700'
                      : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    }`}
                >
                  <span>{r.emoji}</span>
                  <span className="text-[8px] font-medium text-neutral-600 dark:text-neutral-400">{r.count}</span>
                </button>
              ))}

              {/* Add reaction button */}
              {onReaction && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowEmojiPicker(!showEmojiPicker)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setShowEmojiPicker(false)
                    }}
                    className="w-5 h-5 flex items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500 transition-colors"
                    title={t('comments.addReaction')}
                    aria-label={t('comments.addReaction', 'Add reaction')}
                    aria-expanded={showEmojiPicker}
                  >
                    <span className="text-[10px]">+</span>
                  </button>
                  {/* Emoji picker dropdown */}
                  {showEmojiPicker && (
                    <div
                      role="listbox"
                      aria-label={t('comments.addReaction', 'Add reaction')}
                      className="absolute z-50 bottom-full mb-0.5 left-0 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg p-0.5 flex gap-0.5"
                      onKeyDown={(e) => { if (e.key === 'Escape') setShowEmojiPicker(false) }}
                    >
                      {REACTION_EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          role="option"
                          aria-label={emoji}
                          onClick={(e) => {
                            e.stopPropagation()
                            onReaction(comment.id, emoji, true)
                            setShowEmojiPicker(false)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setShowEmojiPicker(false)
                          }}
                          className="w-5 h-5 flex items-center justify-center rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 text-[11px] transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Under-bubble info: visibility + time + resolved + edited */}
          <div className={`flex items-center gap-1 mt-0.5 px-0.5 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
            <span className={`inline-flex px-1 py-0 rounded-full text-[8px] font-medium ${getVisibilityClass(comment.visibility)}`}>
              {getVisibilityLabel(comment, t)}
            </span>

            {comment.resolved && (
              <span className="text-[8px] text-success-500 flex items-center gap-0.5">
                <CheckCircle size={8} />
              </span>
            )}

            {comment.edited_at && !comment.is_deleted && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-[8px] text-neutral-400 italic hover:text-neutral-600 dark:hover:text-neutral-300 cursor-pointer hover:underline"
              >
                {t('comments.edited')}
              </button>
            )}

            {isInactive && (
              <span className="text-[8px] px-1 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-400">
                v{comment.version}
              </span>
            )}

            <span className="text-[8px] text-neutral-400">
              {formatTime(comment.created_at, t)}
            </span>
          </div>

          {/* Edit history popover */}
          {showHistory && contentHistory.length > 0 && (
            <div className="mt-0.5 mx-0.5 p-1.5 rounded-lg bg-neutral-50 dark:bg-neutral-800/80 border border-neutral-200 dark:border-neutral-700 text-[9px]">
              <div className="font-medium text-neutral-600 dark:text-neutral-300 mb-1">
                {t('comments.editHistory', 'Edit history')}
              </div>
              <div className="space-y-1.5">
                {contentHistory.slice().reverse().map((entry, i) => (
                  <div key={i} className="border-l-2 border-neutral-300 dark:border-neutral-600 pl-1.5">
                    <span className="text-[8px] text-neutral-400 block">
                      {new Date(entry.edited_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    <p className="text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap">{entry.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Thread expand/collapse (root comments only) */}
      {!isReply && replyCount > 0 && (
        <div className={`flex ${isMine ? 'justify-end mr-6' : 'justify-start ml-6'}`}>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand?.(comment.id)
            }}
            className="flex items-center gap-0.5 text-[9px] text-primary-600 dark:text-primary-400 hover:underline py-0.5"
          >
            {isExpanded ? (
              <>
                <ChevronDown size={10} />
                {t('comments.hideReplies')}
              </>
            ) : (
              <>
                <ChevronRight size={10} />
                {replyCount === 1
                  ? t('comments.showReply')
                  : t('comments.showReplies', { count: replyCount })}
                {unreadReplyCount > 0 && (
                  <span className="ml-1 px-1.5 py-0 rounded-full bg-primary-500 text-white text-[8px] font-bold animate-pulse">
                    {unreadReplyCount} {t('comments.new', 'ny')}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      )}

      {/* Expanded replies */}
      {!isReply && isExpanded && comment.replies && comment.replies.length > 0 && (
        <div className="mt-0.5 animate-slide-down">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              parties={parties}
              negotiationId={negotiationId}
              currentVersion={currentVersion}
              isAuthor={reply.author?.koblink_id === actorId}
              onResolve={onResolve}
              onDelete={onDelete}
              onClick={onClick}
              onReaction={onReaction}
              isReply
              actorId={actorId}
            />
          ))}
        </div>
      )}
    </div>
  )
}
